// ── Background (non-marker) viewport click ──
// Extracted verbatim from MapCanvas.tsx: clicking empty water deselects the
// current map object, closes the admin radial menu and tears down the gold
// highlight — but only if the pointer did not drag past the move threshold.
//
// The effect body is byte-identical to the original; every closure dependency
// is a parameter under its ORIGINAL name. `getMoveThreshold` and
// `closeAdminRadialMenu` arrive as thunks because in MapCanvas they are `const`
// arrows declared BELOW this hook's call site (calling them at hook-call time
// would hit the TDZ). They are only ever invoked from inside the PIXI event
// handlers, i.e. after render, so the thunk resolves the same binding the
// original closure captured.
import { useEffect, type MutableRefObject } from 'react';
import * as PIXI from 'pixi.js';
import type { MapObject } from '../../models/mapObject';
import { mapSceneSingleton } from './mapScene';
import { asMapMarker, asTickerBound } from './render/mapPixiExtensions';

export interface UseViewportBackgroundClickParams {
  /** Stable map scene bag (groups mutated in place). */
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  setSelectedMapObject: (obj: MapObject | null) => void;
  /** Thunks — see the note above on why these are not passed directly. */
  closeAdminRadialMenu: () => void;
  getMoveThreshold: (event: PIXI.FederatedPointerEvent) => number;
  /** Effect dep — by value. */
  selectedMapObject: MapObject | null;
}

export function useViewportBackgroundClick({
  sceneRef,
  setSelectedMapObject,
  closeAdminRadialMenu,
  getMoveThreshold,
  selectedMapObject,
}: UseViewportBackgroundClickParams): void {
  useEffect(() => {
    if (!sceneRef.current.pixi.viewport) return;

    const viewport = sceneRef.current.pixi.viewport;
    let viewportClickStart: { x: number; y: number; hasMoved: boolean } | null = null;
    let viewportMoveHandler: ((moveEvent: PIXI.FederatedPointerEvent) => void) | null = null;
    
    const handleViewportPointerDown = (event: PIXI.FederatedPointerEvent) => {
      // Якщо режим адміна активний, не обробляємо тут (обробляється в окремому useEffect)
      if (sceneRef.current.flags.godModeActive) return;
      
      const target = event.target;
      const isMarker = target && asMapMarker(target as PIXI.Container)?.mapObjectData;
      
      if (isMarker) return;
      
      viewportClickStart = {
        x: event.global.x,
        y: event.global.y,
        hasMoved: false
      };
      
      viewportMoveHandler = (moveEvent: PIXI.FederatedPointerEvent) => {
        if (!viewportClickStart) return;
        
        const moveX = moveEvent.global.x;
        const moveY = moveEvent.global.y;
        const moveDistance = Math.sqrt(
          Math.pow(moveX - viewportClickStart.x, 2) + 
          Math.pow(moveY - viewportClickStart.y, 2)
        );
        
        const threshold = getMoveThreshold(moveEvent);
        if (moveDistance > threshold) {
          viewportClickStart.hasMoved = true;
        }
      };
      
      viewport.on('pointermove', viewportMoveHandler);
    };
    
    const handleViewportClick = (event: PIXI.FederatedPointerEvent) => {
      const target = event.target;
      const isMarker = target && asMapMarker(target as PIXI.Container)?.mapObjectData;
      
      if (isMarker) {
        if (viewportMoveHandler) {
          viewport.off('pointermove', viewportMoveHandler);
          viewportMoveHandler = null;
        }
        viewportClickStart = null;
        return;
      }
      
      if (viewportClickStart && !viewportClickStart.hasMoved) {
        setSelectedMapObject(null);
        closeAdminRadialMenu();

        if (sceneRef.current.sprites.mapObjectHighlight && sceneRef.current.layers.highlight) {
          const hl = asTickerBound(sceneRef.current.sprites.mapObjectHighlight);
          if (sceneRef.current.pixi.app && hl?.tickerFunction) {
            sceneRef.current.pixi.app.ticker.remove(hl.tickerFunction);
          }
          sceneRef.current.layers.highlight.removeChild(sceneRef.current.sprites.mapObjectHighlight);
          sceneRef.current.sprites.mapObjectHighlight.destroy();
          sceneRef.current.sprites.mapObjectHighlight = null;
          mapSceneSingleton.mapObjectHighlight = null;
        }
      }
      
      if (viewportMoveHandler) {
        viewport.off('pointermove', viewportMoveHandler);
        viewportMoveHandler = null;
      }
      viewportClickStart = null;
    };

    viewport.on('pointerdown', handleViewportPointerDown);
    viewport.on('pointerup', handleViewportClick);

    return () => {
      viewport.off('pointerdown', handleViewportPointerDown);
      viewport.off('pointerup', handleViewportClick);
      if (viewportMoveHandler) {
        viewport.off('pointermove', viewportMoveHandler);
      }
    };
    // selectedMapObject?.id — тригер rebind (тіло selected не читає). closeAdminRadialMenu /
    // getMoveThreshold — TDZ-thunks з MapCanvas (нова стрілка щорендеру) → rebind thrash.
    // sceneRef + setSelectedMapObject стабільні.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selection id + stable refs only
  }, [selectedMapObject?.id, sceneRef, setSelectedMapObject]);
}
